import { Tween, ViewerModule } from '@g.frame/core';
import { Euler, Group, Mesh, MeshBasicMaterial, MeshStandardMaterial, PlaneGeometry, Ray, Sphere, Vector3 } from 'three';
import { PickingController, PickingControllerEventNames } from '@g.frame/common.picking_controller';
import { DesktopModule } from '@g.frame/desktop';
import { OculusQuestModule } from '@g.frame/oculus.quest';
import { FBX_MODEL, Loader, TEXTURE } from '@g.frame/common.loaders';
import { ActionController, ActionControllerEventName } from '@g.frame/common.action_controller';
import { WoodenSignPlaceholderComponent } from "./WoodenSignPlaceholderComponent";
import { WoodenSignComponent } from "./WoodenSignComponent";
import shuffleArray from "../../../utils/Shuffle";
const draggableObjectPositions = shuffleArray([
    new Vector3(0, .55, -1.05),
    new Vector3(0, .425, -1.05),
    new Vector3(0, .3, -1.05),
    new Vector3(0, .175, -1.05),
    new Vector3(0, .05, -1.05),
    new Vector3(0, -.075, -1.05),
    new Vector3(0, -.2, -1.05),
    new Vector3(0, -.325, -1.05),
]);
const placeholdersConfig = [
    new Vector3(0, -.275, -.6),
    new Vector3(0, .3, -.25),
    new Vector3(0, .3, .25),
    new Vector3(0, .3, .75),
    new Vector3(0, -.275, 0),
    new Vector3(0, -.275, .5),
    new Vector3(0, -.275, 1),
    new Vector3(0, .3, 1.25),
];
const leavesConfig = [
    {
        name: 'Corn',
        modelName: 'corn_leaf',
        position: new Vector3(0, .2, -0.6),
        scale: 2.25,
        rotation: new Euler(0, -Math.PI / 2, 0)
    },
    {
        name: 'Cotton',
        modelName: 'cotton_leaf',
        position: new Vector3(-0.002, 0.4875, -0.25),
        scale: 1.2,
        rotation: new Euler(0, -Math.PI / 2, 0)
    },
    {
        name: 'Cucumber',
        modelName: 'cucumber_leaf',
        position: new Vector3(0, 0.475, 0.25),
        scale: 1.2,
        rotation: new Euler(0, -Math.PI / 2, 0)
    },
    {
        name: 'Lettuce',
        modelName: 'lettuce_leaf',
        position: new Vector3(0, 0.475, .75),
        scale: 1.2,
        rotation: new Euler(0, -Math.PI / 2, 0)
    },
    {
        name: 'Peanut',
        modelName: 'peanut_leaf',
        position: new Vector3(0, -0.05, 0),
        scale: 1.2,
        rotation: new Euler(0, -Math.PI / 2, 0)
    },
    {
        name: 'Potato',
        modelName: 'potato_leaf',
        position: new Vector3(0, -0.05, 0.5),
        scale: 1.2,
        rotation: new Euler(0, -Math.PI / 2, 0)
    },
    {
        name: 'Soy',
        modelName: 'soy_leaf',
        position: new Vector3(0, -0.05, 1),
        scale: 1.2,
        rotation: new Euler(0, -Math.PI / 2, 0)
    },
    {
        name: 'Tomato',
        modelName: 'tomato_leaf',
        position: new Vector3(-.003, 0.5, 1.25),
        rotation: new Euler(Math.PI, 0, Math.PI / 2),
        scale: 0.0125,
    }
];
export default class LeavesQuizModule extends ViewerModule {
    // private menuConfig: IMC;
    constructor(modulesProcessor, uiManager) {
        var _a;
        super();
        this.modulesProcessor = modulesProcessor;
        this.uiManager = uiManager;
        this.goalList = new Array(draggableObjectPositions.length);
        this.draggableObjectList = new Array(draggableObjectPositions.length);
        this.leavesList = new Array(draggableObjectPositions.length);
        this.uiContainer = new Group();
        this.addObject(this.uiContainer);
        this.uiObject.rotation.set(0, Math.PI / 2, 0);
        this.goalContainer = new Group();
        this.uiContainer.add(this.goalContainer);
        this.controls = modulesProcessor.modulesInstances.get(DesktopModule)[1];
        this.actionController = modulesProcessor.agents.get(ActionController);
        this.pickingController = modulesProcessor.agents.get(PickingController);
        this.loader = modulesProcessor.agents.get(Loader);
        this.isVR = !!((_a = modulesProcessor.modulesInstances.get(OculusQuestModule)) === null || _a === void 0 ? void 0 : _a.length);
        // this.menuConfig = {
        //     position: new Vector3(-48, 4, -17),
        //     rotation: new Euler(0, Math.PI / 2, 0),
        //     scale: 1,
        //     vr_position: new Vector3(-48, 4, -17),
        //     vr_rotation: new Euler(0, Math.PI / 2, 0),
        //     vr_scale: 1
        // };
        for (let index = 0; index < this.goalList.length; index++) {
            this.goalList[index] = new WoodenSignPlaceholderComponent(this.loader);
            this.goalList[index].uiObject.position.copy(placeholdersConfig[index]);
            this.goalList[index].uiObject.rotation.set(0, -Math.PI / 2, 0);
            this.goalContainer.add(this.goalList[index].uiObject);
        }
        // console.log(this.goalList);
        for (let index = 0; index < this.leavesList.length; index++) {
            this.loader.addResources([
                {
                    name: leavesConfig[index].modelName + '_model',
                    type: FBX_MODEL,
                    url: require(`../../../../assets/models/leaves/${leavesConfig[index].modelName}.fbx`)
                }, {
                    name: leavesConfig[index].modelName + '_bc_texture',
                    type: TEXTURE,
                    url: require(`../../../../assets/textures/leaves/${leavesConfig[index].modelName}_color_map.jpg`)
                }, {
                    name: leavesConfig[index].modelName + '_op_texture',
                    type: TEXTURE,
                    url: require(`../../../../assets/textures/leaves/${leavesConfig[index].modelName}_opacity_map.jpg`)
                },
            ]);
        }
        for (let index = 0; index < this.draggableObjectList.length; index++) {
            const draggableObject = new WoodenSignComponent(this.loader, leavesConfig[index].name);
            this.draggableObjectList[index] = draggableObject;
            draggableObject.uiObject.position.copy(draggableObjectPositions[index]);
            draggableObject.uiObject.rotation.set(0, -Math.PI / 2, 0);
            draggableObject.uiObject.userData.startPosition = draggableObject.uiObject.position.clone();
            draggableObject.uiObject.userData.startQuaternion = draggableObject.uiObject.quaternion.clone();
            draggableObject.uiObject.userData.endQuaternion = this.goalList[index].uiObject.quaternion.clone();
            draggableObject.uiObject.userData.pickingMoveDistance = 1.5;
            draggableObject.uiObject.userData.order = index;
            this.uiContainer.add(this.draggableObjectList[index].uiObject);
        }
        // console.log('this.draggableObjectList', this.draggableObjectList);
    }
    init() {
        this.initUI();
        this.goalList.forEach((goalObject, index) => {
            goalObject.init();
        });
        this.draggableObjectList.forEach(draggable => {
            draggable.init();
        });
        for (let index = 0; index < this.leavesList.length; index++) {
            const leave = this.loader.getResource(leavesConfig[index].modelName + '_model');
            // @ts-ignore
            leave.children[0].material = new MeshStandardMaterial({
                map: this.loader.getResource(leavesConfig[index].modelName + '_bc_texture'),
                alphaMap: this.loader.getResource(leavesConfig[index].modelName + '_op_texture'),
                transparent: true
            });
            // @ts-ignore
            leave.children[0].material.map.encoding = 3000;
            leave.position.copy(leavesConfig[index].position);
            leave.scale.setScalar(leavesConfig[index].scale);
            leave.rotation.copy(leavesConfig[index].rotation);
            this.goalContainer.add(leave);
            this.leavesList[index] = leave;
        }
        // console.log('this.leavesList', this.leavesList);
        this.showElements(true);
    }
    initUI() {
        this.tryAgainBtn = new Mesh(new PlaneGeometry(1.04 / 2.4 * 3.6, 1.04 / 2.4), new MeshBasicMaterial({
            map: this.loader.getResource('LOBBY_try_again'),
            transparent: true
        }));
        // this.actionController.on(ActionControllerEventName.click, this.tryAgainBtn, () => UIStageManager.hidePopUpScreen());
        this.nextStepBtn = new Mesh(new PlaneGeometry(1.04 * 3.5 / 2.4, 1.04 / 2.4), new MeshBasicMaterial({
            map: this.loader.getResource('LOBBY_continue_btn'),
            transparent: true
        }));
        this.actionController.on(ActionControllerEventName.click, this.nextStepBtn, () => {
            // UIStageManager.hidePopUpScreen();
            this.fire('finish');
        });
    }
    enablePicking(b = true) {
        this.draggableObjectList.forEach(draggableObject => {
            this.pickingController.off(PickingControllerEventNames.RELEASED, draggableObject.uiObject);
            this.pickingController.off(PickingControllerEventNames.PICKED, draggableObject.uiObject);
        });
        if (!b)
            return;
        this.draggableObjectList.forEach((draggableObject, draggableIndex) => {
            let callbackMove;
            draggableObject.pickedCallback = null;
            this.pickingController.on(PickingControllerEventNames.PICKED, draggableObject.uiObject, draggableObject.pickedCallback = (event) => {
                if (this.checkWrongPlacement() && !draggableObject.highlighted) {
                    this.pickingController.forceRelease(draggableObject.uiObject.position, draggableObject.uiObject.quaternion, true, 1);
                }
                else {
                    const pickingRotation = new Euler(0, -Math.PI / 2, 0);
                    this.pickingController.on(PickingControllerEventNames.MOVED, draggableObject.uiObject, callbackMove = (event) => {
                        if (!this.isVR) {
                            draggableObject.uiObject.rotation.copy(pickingRotation);
                            draggableObject.uiObject.updateMatrixWorld(true);
                        }
                        else {
                            const telecinese = new Vector3(-0.15, 0, 0.05);
                            draggableObject.uiObject.position.add(telecinese);
                            draggableObject.uiObject.updateMatrixWorld(true);
                        }
                    });
                }
            });
            this.pickingController.on(PickingControllerEventNames.RELEASED, draggableObject.uiObject, (event) => {
                this.pickingController.off(PickingControllerEventNames.MOVED, draggableObject.uiObject, callbackMove);
                draggableObject.container.rotation.y = 0;
                draggableObject.uiObject.updateMatrixWorld(true);
                const position = draggableObject.uiObject.localToWorld(new Vector3());
                const goalObject = this.goalList.filter(goalObject => !goalObject.occupied)
                    .sort((vec3_a, vec3_b) => vec3_a.uiObject.getWorldPosition(new Vector3()).distanceTo(position) - vec3_b.uiObject.getWorldPosition(new Vector3()).distanceTo(position))[0];
                let error = false, releasedCorrect = false;
                // let mountainPosition = goalObject.uiObject.getWorldPosition(new Vector3());
                if (this.isVR) {
                    for (let goalIndex = 0; goalIndex < this.goalList.length; goalIndex++) {
                        const goalObject = this.goalList[goalIndex];
                        if (goalObject.occupied)
                            continue;
                        goalObject.uiObject.updateWorldMatrix(true, true);
                        if (!releasedCorrect) {
                            releasedCorrect = (position.distanceTo(goalObject.uiObject.localToWorld(new Vector3())) < 0.3);
                            if (releasedCorrect) {
                                if (draggableObject.uiObject.userData.releasedGoal) {
                                    draggableObject.uiObject.userData.releasedGoal.occupied = false;
                                    draggableObject.uiObject.userData.releasedGoal.uiObject.visible = true;
                                }
                                error = (draggableIndex !== goalIndex);
                                goalObject.occupied = true;
                                draggableObject.uiObject.userData.releasedGoal = goalObject;
                                if (!error) {
                                    // mountainPosition = pos;
                                    // mountainPosition.y += 0.001;
                                    this.checkFinish();
                                }
                            }
                        }
                    }
                }
                else {
                    const cameraPosition = this.controls.object.localToWorld(new Vector3());
                    for (let goalIndex = 0; goalIndex < this.goalList.length; goalIndex++) {
                        const goalObject = this.goalList[goalIndex];
                        if (goalObject.occupied)
                            continue;
                        goalObject.uiObject.updateWorldMatrix(true, true);
                        if (!releasedCorrect) {
                            const pos = goalObject.uiObject.getWorldPosition(new Vector3());
                            const sphere = new Sphere(pos, 0.25);
                            const ray = new Ray(cameraPosition, draggableObject.uiObject.getWorldPosition(new Vector3()).sub(cameraPosition).normalize());
                            const intersection = new Vector3();
                            releasedCorrect = !!ray.intersectSphere(sphere, intersection);
                            if (releasedCorrect) {
                                if (draggableObject.uiObject.userData.releasedGoal) {
                                    draggableObject.uiObject.userData.releasedGoal.occupied = false;
                                    draggableObject.uiObject.userData.releasedGoal.uiObject.visible = true;
                                }
                                error = (draggableIndex !== goalIndex);
                                goalObject.occupied = true;
                                draggableObject.uiObject.userData.releasedGoal = goalObject;
                                if (!error) {
                                    // mountainPosition = pos;
                                    // mountainPosition.y += 0.001;
                                    this.checkFinish();
                                }
                            }
                        }
                    }
                }
                if (releasedCorrect) {
                    this.flyTo(draggableObject.uiObject, draggableObject.uiObject.parent.worldToLocal(draggableObject.uiObject.userData.releasedGoal.uiObject.getWorldPosition(new Vector3()).clone()), draggableObject.uiObject.userData.endQuaternion.clone());
                    draggableObject.uiObject.userData.releasedGoal.uiObject.visible = false;
                    if (!error) {
                        draggableObject.highlight(false);
                        draggableObject.pickedCallback = null;
                        // this.resumePicking(draggableIndex);
                        this.pickingController.off(PickingControllerEventNames.RELEASED, draggableObject.uiObject);
                        this.pickingController.off(PickingControllerEventNames.PICKED, draggableObject.uiObject);
                    }
                    else {
                        // this.pausePicking(draggableIndex);
                        draggableObject.highlight(true);
                    }
                }
                else {
                    if (draggableObject.uiObject.userData.releasedGoal) {
                        this.flyTo(draggableObject.uiObject, draggableObject.uiObject.parent.worldToLocal(draggableObject.uiObject.userData.releasedGoal.uiObject.getWorldPosition(new Vector3()).clone()), draggableObject.uiObject.userData.endQuaternion.clone());
                        draggableObject.uiObject.userData.releasedGoal.uiObject.visible = false;
                        draggableObject.highlight(true);
                    }
                    else {
                        draggableObject.highlight(false);
                        this.flyTo(draggableObject.uiObject, draggableObject.uiObject.userData.startPosition.clone(), draggableObject.uiObject.userData.startQuaternion.clone());
                    }
                }
            });
        });
    }
    // pausePicking(exceptionIndex?: number) {
    //     this.draggableObjectList.forEach((draggableObject, draggableIndex) => {
    //         if (exceptionIndex !== draggableIndex) {
    //             this.pickingController.off(PickingControllerEventNames.PICKED, draggableObject.uiObject)
    //         }
    //     });
    // }
    //
    // resumePicking(exceptionIndex?: number) {
    //     this.draggableObjectList.forEach((draggableObject, draggableIndex) => {
    //         if (exceptionIndex !== draggableIndex) {
    //             if (draggableObject.pickedCallback) {
    //                 this.pickingController.on(PickingControllerEventNames.PICKED, draggableObject.uiObject, draggableObject.pickedCallback);
    //              }
    //         }
    //     });
    // }
    checkFinish() {
        const objectsLeft = this.goalList.filter(goalObject => !goalObject.occupied).length;
        if (objectsLeft === 0) {
            this.fire('done');
        }
    }
    checkWrongPlacement() {
        const wrongPlaced = this.draggableObjectList.find(draggableObject => draggableObject.highlighted);
        // console.log(!!wrongPlaced);
        return (!!wrongPlaced);
    }
    flyTo(object, position, quaternion) {
        const oldQuaternion = object.quaternion.clone();
        new Tween(object.position)
            .to(position, 500)
            // .easing(Tween.Easing.Cubic.Out)
            .onUpdate((i) => {
            object.quaternion.copy(oldQuaternion).slerp(quaternion, i);
        })
            .start();
    }
    enableModule(isEnable = true) {
        this.showElements(isEnable);
        this.enablePicking(isEnable);
        this.controls.enabled = true;
        // isEnable && this.lobby.menuPanel.updatePosition(this.menuConfig);
    }
    showElements(isShow = true) {
        [...this.goalList, ...this.draggableObjectList].forEach(element => {
            element.uiObject.visible = isShow;
        });
        this.goalContainer.children.forEach(element => {
            element.visible = isShow;
        });
    }
    reset() {
        shuffleArray(draggableObjectPositions);
        this.goalList.forEach(goalObject => {
            goalObject.uiObject.visible = true;
            goalObject.occupied = false;
        });
        this.draggableObjectList.forEach((draggableObject, index) => {
            draggableObject.uiObject.position.copy(draggableObjectPositions[index]);
            draggableObject.uiObject.userData.startPosition = draggableObject.uiObject.position.clone();
            draggableObject.uiObject.userData.releasedGoal = null;
            draggableObject.highlight(false);
        });
    }
}
